import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, ExtensionAuthBridge, RequireAuth, SignInScreen, SignUpScreen } from "@/lib/clerk";
import { ThemeProvider, useTheme } from "@/lib/theme";
import Landing from "@/pages/Landing";
import AppLayout from "@/components/app/AppLayout";
import Dashboard from "@/pages/Dashboard";
import AnalysisReport from "@/pages/AnalysisReport";
import Compare from "@/pages/Compare";
import Collections from "@/pages/Collections";
import Bulk from "@/pages/Bulk";
import Credits from "@/pages/Credits";
const Checkout = React.lazy(() => import("@/pages/Checkout").then(m => ({ default: m.Checkout })));
const CheckoutReturn = React.lazy(() => import("@/pages/Checkout").then(m => ({ default: m.CheckoutReturn })));
import Settings from "@/pages/Settings";
import SharedCollection from "@/pages/SharedCollection";
import { Privacy, Terms } from "@/pages/Legal";
import { AnalyticsProvider } from "@/lib/analytics";
import { AnalyticsConsentBanner } from "@/components/AnalyticsConsent";

function App() {
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        if (ref) {
            localStorage.setItem("spotcar-referral", ref);
            params.delete("ref");
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
            window.history.replaceState(null, "", newUrl);
        }
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <AnalyticsProvider>
                        <ExtensionAuthBridge />
                        <Routes>
                        {/* Public marketing */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />

                        {/* Auth */}
                        <Route path="/sign-in/*" element={<SignInScreen />} />
                        <Route path="/sign-up/*" element={<SignUpScreen />} />

                        {/* Public shared */}
                        <Route path="/shared/:token" element={<SharedCollection />} />
                        <Route
                            path="/shared/:token/:id"
                            element={<AnalysisReport shared />}
                        />

                        {/* App (auth required) */}
                        <Route
                            path="/app"
                            element={
                                <RequireAuth>
                                    <AppLayout />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="new" element={<Navigate to="/app/bulk" replace />} />
                            <Route path="analysis/:id" element={<AnalysisReport />} />
                            <Route path="compare" element={<Compare />} />
                            <Route path="collections" element={<Collections />} />
                            <Route path="bulk" element={<Bulk />} />
                            <Route path="credits" element={<Credits />} />
                            <Route
                                path="checkout/:packageId"
                                element={<React.Suspense fallback={null}><Checkout /></React.Suspense>}
                            />
                            <Route
                                path="checkout/return"
                                element={<React.Suspense fallback={null}><CheckoutReturn /></React.Suspense>}
                            />
                            <Route path="settings" element={<Settings />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <AnalyticsConsentBanner />
                        </AnalyticsProvider>
                    </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
            <ThemedToaster />
        </div>
    );
}

function ThemedToaster() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    return (
        <Toaster
            position="bottom-right"
            theme={isDark ? "dark" : "light"}
            toastOptions={{
                style: {
                    border: `1px solid ${isDark ? "#3f3f46" : "#d4d4d8"}`,
                    borderRadius: 6,
                    background: isDark ? "#18181b" : "#ffffff",
                    color: isDark ? "#fafafa" : "#18181b",
                    fontFamily: "IBM Plex Mono, monospace",
                    boxShadow: isDark
                        ? "0 4px 12px rgba(0,0,0,0.5)"
                        : "0 4px 12px rgba(0,0,0,0.08)",
                },
            }}
        />
    );
}

export default App;
